import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import BaseModal from "../../components/modals/BaseModal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import Toast from "../../components/ui/Toast";
import Badge from "../../components/ui/Badge";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth";
import hrFormService from "../../api/hrFormService";
import requestService from "../../api/requestService";
import {
  Download,
  FileDown,
  Calendar,
  Trash2,
  CheckSquare,
  Square,
  RefreshCw,
  Lock,
  User,
  Clock,
  Briefcase,
  Home,
  Umbrella,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  Settings,
} from "lucide-react";
import { motion } from "framer-motion";
import * as XLSX from "xlsx-js-style";
import { formatDateLocal, parseDateLocal } from "../../utils/dateUtils";
import { isAdmin as checkIsAdmin } from "../../utils/permissions";

const HRForm = () => {
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { auth } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin (high_potit team)
  const isAdmin = checkIsAdmin(auth?.employee);

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin) {
      navigate("/unauthorized");
    }
  }, [isAdmin, navigate]);

  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWeekExported, setIsWeekExported] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [customRange, setCustomRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [exportedWeeksData, setExportedWeeksData] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({
    key: "startDate",
    dir: "asc",
  });

  // 0 = current week, -1 = previous week (only allowed to go 1 week back)
  const [weekOffset, setWeekOffset] = useState(0);

  // Monthly report states
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Delete confirmation (HR form rows)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Helper: get start of a week by offset (0 = current, -1 = previous)
  const getWeekStartByOffset = (offset) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + offset * 7);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // Check if the VIEWED week is in the past (should be CLOSED)
  const isCurrentWeekClosed = useMemo(() => {
    const viewedStart = getWeekStartByOffset(weekOffset);
    const endOfWeek = new Date(viewedStart);
    endOfWeek.setDate(viewedStart.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return new Date() > endOfWeek;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  // Check export status for the currently viewed week
  const checkWeekExportStatus = async (offset = weekOffset) => {
    try {
      const weekStartStr = formatDateLocal(getWeekStartByOffset(offset));
      const response = await hrFormService.checkWeekExported(weekStartStr);
      setIsWeekExported(response.isExported || false);
    } catch (error) {
      console.error("Failed to check export status:", error);
      setIsWeekExported(false);
    }
  };

  // Fetch HR Form data for the given week offset (0 = current, -1 = previous)
  const fetchHRFormData = async (offset = weekOffset) => {
    try {
      setLoading(true);
      const weekStartStr = formatDateLocal(getWeekStartByOffset(offset));
      const response = await hrFormService.getHRFormData(weekStartStr);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setAllData(rows);
      await checkWeekExportStatus(offset);
    } catch (error) {
      console.error("Failed to fetch HR Form data:", error);
      showError(error.response?.data?.message || "Failed to load HR Form data");
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount (current week)
  useEffect(() => {
    fetchHRFormData(0);
    fetchMonthlyReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter data to the currently viewed week (accounts for weekOffset)
  const getCurrentWeekData = useMemo(() => {
    const startOfWeek = getWeekStartByOffset(weekOffset);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return allData.filter((item) => {
      if (!item || item.startDate == null) return false;
      const itemDate = parseDateLocal(String(item.startDate));
      if (isNaN(itemDate.getTime())) return false;
      return itemDate >= startOfWeek && itemDate <= endOfWeek;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, weekOffset]);

  // Get previous forms (older than current week)
  const getPreviousFormsData = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    return allData.filter((item) => {
      if (!item || item.startDate == null) return false;
      const itemDate = parseDateLocal(String(item.startDate));
      if (isNaN(itemDate.getTime())) return false;
      return itemDate < startOfWeek;
    });
  }, [allData]);

  // Get current week date range for display
  // Range label for the currently viewed week
  const getCurrentWeekRange = () => {
    const startOfWeek = getWeekStartByOffset(weekOffset);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const fmt = (date) =>
      date.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      });

    return `${fmt(startOfWeek)} - ${fmt(endOfWeek)}`;
  };

  // Navigate to previous week (only one step back allowed)
  const handlePreviousWeek = () => {
    const newOffset = -1;
    setWeekOffset(newOffset);
    setSelectedRows(new Set());
    fetchHRFormData(newOffset);
  };

  // Navigate back to current week
  const handleCurrentWeek = () => {
    const newOffset = 0;
    setWeekOffset(newOffset);
    setSelectedRows(new Set());
    fetchHRFormData(newOffset);
  };

  // Format date with weekday name
  const formatDateWithWeekday = (dateString) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const formatted = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    return `${weekday} - ${formatted}`;
  };

  // Fetch and calculate monthly reports
  const fetchMonthlyReports = async (startDate = null, endDate = null) => {
    try {
      setLoading(true);

      // If no date range provided, get last 6 months
      let start = startDate;
      let end = endDate;

      if (!start || !end) {
        const now = new Date();
        end = formatDateLocal(now);
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        start = formatDateLocal(sixMonthsAgo);
      }

      const response = await hrFormService.getHRFormData(start);
      const allFormsData = response.data || [];

      // Filter by end date if provided
      const filteredData = allFormsData.filter((item) => {
        const itemDate = new Date(item.startDate);
        const endDateObj = new Date(end);
        return itemDate <= endDateObj;
      });

      // Group data by month and calculate stats
      const monthlyData = calculateMonthlyStats(filteredData);
      console.log("Monthly reports calculated:", monthlyData);
    } catch (error) {
      console.error("Failed to fetch monthly reports:", error);
      showError("Failed to load monthly reports");
    } finally {
      setLoading(false);
    }
  };

  // Calculate monthly statistics
  const calculateMonthlyStats = (data) => {
    const monthMap = new Map();

    data.forEach((item) => {
      const itemDate = new Date(item.startDate);
      const monthKey = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, "0")}`;
      const monthName = itemDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          monthKey,
          monthName,
          wfhCount: 0,
          vacationCount: 0,
          earlyLeaveCount: 0,
          latePermissionCount: 0,
          employees: new Map(),
          allForms: [],
        });
      }

      const monthData = monthMap.get(monthKey);
      monthData.allForms.push(item);

      // Count by type
      const type = item.timeOffType?.toLowerCase() || "";
      if (type.includes("wfh") || type.includes("work from home")) {
        monthData.wfhCount++;
      } else if (type.includes("vacation") || type.includes("إجازة")) {
        monthData.vacationCount++;
      } else if (type.includes("early") || type.includes("مبكر")) {
        monthData.earlyLeaveCount++;
      } else if (type.includes("late") || type.includes("متأخر")) {
        monthData.latePermissionCount++;
      }

      // Track employee data
      const empId = item.employeeFingerprint || item.employee;
      if (!monthData.employees.has(empId)) {
        monthData.employees.set(empId, {
          name: item.employee,
          fingerprint: item.employeeFingerprint,
          jobPosition: item.jobPosition,
          branch: item.branch,
          wfh: [],
          vacation: [],
          earlyLeave: [],
          latePermission: [],
          mission: [],
        });
      }

      const empData = monthData.employees.get(empId);
      if (type.includes("wfh") || type.includes("work from home")) {
        empData.wfh.push(item);
      } else if (type.includes("vacation") || type.includes("إجازة")) {
        empData.vacation.push(item);
      } else if (type.includes("early") || type.includes("مبكر")) {
        empData.earlyLeave.push(item);
      } else if (type.includes("late") || type.includes("متأخر")) {
        empData.latePermission.push(item);
      } else if (type.includes("mission") || type.includes("ماموريه")) {
        empData.mission.push(item);
      }
    });

    // Convert to array and sort by date (newest first)
    return Array.from(monthMap.values())
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
      .map((month) => ({
        ...month,
        employees: Array.from(month.employees.values()),
      }));
  };

  // sort current week data by start date (oldest first), then by end date (oldest first) so the display is consistent and logical
  // const date = getCurrentWeekDate;
  const handleDateSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  const data = [...getCurrentWeekData].sort((a, b) => {
    const diff = new Date(a[sortConfig.key]) - new Date(b[sortConfig.key]);
    return sortConfig.dir === "asc" ? diff : -diff;
  });

  const columns = [
    { key: "code", header: "CODE" },
    { key: "employeeFingerprint", header: "EMPLOYEE / FINGERPRINT" },
    { key: "employee", header: "EMPLOYEE" },
    { key: "jobPosition", header: "EMPLOYEE / JOB POSITION" },
    { key: "branch", header: "EMPLOYEE / الفرع" },
    { key: "timeOffType", header: "TIME OFF TYPE" },
    { key: "reason", header: "العرض عن النموذج الإداري" },
    { key: "startDate", header: "START DATE" },
    { key: "endDate", header: "END DATE" },
    { key: "days", header: "عدد الأيام" },
  ];

  const handleCellClick = (rowId, columnKey, currentValue) => {
    // Prevent editing if week is closed or exported
    if (isCurrentWeekClosed || isWeekExported) {
      showError("This week is closed and cannot be edited");
      return;
    }
    setEditingCell({ rowId, columnKey });
    setEditValue(currentValue);
  };

  const handleCellBlur = () => {
    if (editingCell) {
      const { rowId, columnKey } = editingCell;
      setAllData((prevData) =>
        prevData.map((row) =>
          row.id === rowId ? { ...row, [columnKey]: editValue } : row,
        ),
      );
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCellBlur();
    } else if (e.key === "Escape") {
      setEditingCell(null);
    }
  };

  // Row selection handlers
  const handleSelectRow = (rowId) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === data.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(data.map((row) => row.id)));
    }
  };

  const handleDeleteSelected = async () => {
    // Prevent deletion if week is closed or exported
    if (isCurrentWeekClosed || isWeekExported) {
      showError("This week is closed and cannot be modified");
      return;
    }

    if (selectedRows.size === 0) {
      showError("Please select at least one row to delete");
      return;
    }

    try {
      // Delete underlying requests (admin-only API)
      await Promise.all(
        Array.from(selectedRows).map((id) => requestService.deleteRequest(id)),
      );

      showSuccess("Selected forms and requests deleted successfully");

      // Refresh HR Form data from backend
      await fetchHRFormData(weekOffset);
      setSelectedRows(new Set());
    } catch (error) {
      console.error("Failed to delete selected HR forms:", error);
      showError(
        error.response?.data?.message ||
          "Failed to delete selected forms and requests",
      );
    }
  };

  // XLSX Export function with professional styling
  const buildWorkbook = (dataToExport) => {
    // Prepare data for Excel with header row
    const worksheetData = [
      columns.map((col) => col.header), // Header row
      ...dataToExport.map((row) =>
        columns.map((col) => {
          const value = row[col.key];
          return value === null || value === undefined ? "" : value;
        }),
      ),
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Define border style
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // Define header style - Orange accent color (#E68005)
    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
        sz: 11,
        name: "Calibri",
      },
      fill: {
        fgColor: { rgb: "E68005" }, // Orange accent
        patternType: "solid",
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "medium", color: { rgb: "B87004" } },
        bottom: { style: "medium", color: { rgb: "B87004" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    // Define data cell style
    const dataCellStyle = {
      font: {
        color: { rgb: "333333" },
        sz: 10,
        name: "Calibri",
      },
      alignment: {
        horizontal: "left",
        vertical: "center",
        wrapText: true,
      },
      border: borderStyle,
    };

    // Define alternate row style for better readability
    const alternateRowStyle = {
      ...dataCellStyle,
      fill: {
        fgColor: { rgb: "F9F9F9" },
        patternType: "solid",
      },
    };

    // Get the range of the worksheet
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Apply styles to all cells
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });

        // Initialize cell if it doesn't exist
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: "s", v: "" };
        }

        // Apply header style to first row
        if (row === 0) {
          ws[cellAddress].s = headerStyle;
        } else {
          // Apply alternate row styling for data rows
          if (row % 2 === 0) {
            ws[cellAddress].s = alternateRowStyle;
          } else {
            ws[cellAddress].s = dataCellStyle;
          }
        }
      }
    }

    // Set optimized column widths based on content
    const colWidths = columns.map((col) => {
      const headerLength = col.header.length;
      let maxContentLength = headerLength;

      dataToExport.forEach((row) => {
        const value = String(row[col.key] || "").length;
        if (value > maxContentLength) maxContentLength = value;
      });

      if (col.key === "code" || col.key === "days")
        return { wch: Math.max(12, maxContentLength + 3) };
      if (col.key === "startDate" || col.key === "endDate")
        return { wch: Math.max(16, maxContentLength + 3) };
      if (col.key === "employeeFingerprint")
        return { wch: Math.max(20, maxContentLength + 3) };
      return { wch: Math.min(Math.max(22, maxContentLength + 4), 45) };
    });
    ws["!cols"] = colWidths;

    // Set row heights
    ws["!rows"] = [
      { hpt: 25 }, // Header row height
      ...dataToExport.map(() => ({ hpt: 18 })), // Data rows height
    ];

    // Freeze header row for better navigation
    ws["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomLeft",
    };

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "HR Forms");

    return wb;
  };

  const exportToXLSX = (dataToExport, filename) => {
    if (dataToExport.length === 0) {
      alert("No data to export");
      return;
    }
    XLSX.writeFile(buildWorkbook(dataToExport), filename);
  };

  // Email the viewed week (as shown, including inline edits) as text + Excel
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isMailSettingsOpen, setIsMailSettingsOpen] = useState(false);
  const [mailSettings, setMailSettings] = useState({ to: [""], cc: [""], bcc: [""] });
  const [savingMailSettings, setSavingMailSettings] = useState(false);
  const cleanEmails = (list) => list.map((e) => e.trim()).filter(Boolean);
  const setEmailAt = (key, i, value) =>
    setMailSettings((s) => ({ ...s, [key]: s[key].map((e, idx) => (idx === i ? value : e)) }));
  const addEmailRow = (key) => setMailSettings((s) => ({ ...s, [key]: [...s[key], ""] }));
  const removeEmailRow = (key, i) =>
    setMailSettings((s) => ({ ...s, [key]: s[key].length > 1 ? s[key].filter((_, idx) => idx !== i) : [""] }));
  const handleOpenMailSettings = async () => {
    setIsMailSettingsOpen(true);
    try {
      const s = await hrFormService.getMailSettings();
      setMailSettings({ to: s.to.length ? s.to : [""], cc: s.cc.length ? s.cc : [""], bcc: s.bcc?.length ? s.bcc : [""] });
    } catch (error) {
      showError(error.response?.data?.message || "Failed to load mail settings");
    }
  };
  const handleSaveMailSettings = async () => {
    setSavingMailSettings(true);
    try {
      await hrFormService.updateMailSettings({
        to: cleanEmails(mailSettings.to),
        cc: cleanEmails(mailSettings.cc),
        bcc: cleanEmails(mailSettings.bcc),
      });
      showSuccess("Mail recipients saved");
      setIsMailSettingsOpen(false);
    } catch (error) {
      showError(error.response?.data?.message || "Failed to save mail recipients");
    } finally {
      setSavingMailSettings(false);
    }
  };

  const handleSendEmail = async () => {
    if (data.length === 0 || sendingEmail) return;
    const weekStart = getWeekStartByOffset(weekOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    setSendingEmail(true);
    try {
      const fileBase64 = XLSX.write(buildWorkbook(data), { bookType: "xlsx", type: "base64" });
      await hrFormService.sendHRFormEmail({
        weekStart: formatDateLocal(weekStart),
        weekEnd: formatDateLocal(weekEnd),
        headers: columns.map((c) => c.header),
        rows: data.map((row) => columns.map((c) => (row[c.key] ?? "").toString())),
        fileName: `HR_Form_${formatDateLocal(weekStart)}_to_${formatDateLocal(weekEnd)}.xlsx`,
        fileBase64,
      });
      showSuccess("HR form emailed successfully");
    } catch (error) {
      showError(error.response?.data?.message || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportCurrentWeek = async () => {
    const weekStart = getWeekStartByOffset(weekOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const startStr = weekStart.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const endStr = weekEnd.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const label = weekOffset === 0 ? "Current_Week" : "Previous_Week";

    // Export to Excel
    exportToXLSX(data, `HR_Form_${label}_${startStr}_to_${endStr}.xlsx`);

    // Mark week as exported (lock it)
    if (!isWeekExported) {
      try {
        const weekStartStr = formatDateLocal(weekStart);
        await hrFormService.exportWeek(weekStartStr);
        setIsWeekExported(true);
        showSuccess("Week exported and locked successfully");
      } catch (error) {
        console.error("Failed to mark week as exported:", error);
        showError(error.response?.data?.message || "Failed to lock week");
      }
    }
  };

  // Get available weeks from previous forms + exported weeks from DB
  const getAvailableWeeks = useMemo(() => {
    const weekMap = new Map();

    if (getPreviousFormsData.length > 0) {
      getPreviousFormsData.forEach((item) => {
        const itemDate = new Date(item.startDate);
        const weekStart = new Date(itemDate);
        weekStart.setDate(itemDate.getDate() - itemDate.getDay()); // Start of week (Sunday)
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const weekKey = formatDateLocal(weekStart);
        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, {
            start: weekStart,
            end: weekEnd,
            count: 0,
            status: "available",
          });
        }
        weekMap.get(weekKey).count++;
      });
    }

    // Also mark weeks that were previously exported as available
    exportedWeeksData.forEach((exp) => {
      const weekStart = new Date(exp.weekStart);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(exp.weekEnd);
      weekEnd.setHours(23, 59, 59, 999);
      const weekKey = formatDateLocal(weekStart);
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          start: weekStart,
          end: weekEnd,
          count: exp.requestCount || 0,
          status: "available",
        });
      }
    });

    // Generate list of previous weeks (last 12 weeks) with status
    const weeksList = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() - i * 7);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const weekKey = formatDateLocal(weekStart);
      const existingWeek = weekMap.get(weekKey);

      weeksList.push({
        start: weekStart,
        end: weekEnd,
        weekKey,
        count: existingWeek?.count || 0,
        status: existingWeek ? "available" : "not_available",
        label: `${weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${weekEnd.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`,
      });
    }

    return weeksList.sort((a, b) => b.start - a.start);
  }, [getPreviousFormsData, exportedWeeksData]);

  const handleOpenDateRangeModal = async () => {
    setIsDateRangeModalOpen(true);
    try {
      const response = await hrFormService.getExportedWeeks();
      const weeks = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setExportedWeeksData(weeks);
    } catch (error) {
      console.error("Failed to fetch exported weeks:", error);
      setExportedWeeksData([]);
    }
  };

  // Sync availableWeeks state whenever the computed list changes
  useEffect(() => {
    setAvailableWeeks(getAvailableWeeks);
  }, [getAvailableWeeks]);

  const handleExportWithDateRange = async () => {
    const hasCustomRange = customRange.startDate && customRange.endDate;
    const hasWeeks = selectedWeeks.size > 0;

    if (!hasCustomRange && !hasWeeks) {
      alert("Please select at least one week or provide a custom date range");
      return;
    }

    let allRows = [];
    let startLabel = "";
    let endLabel = "";

    try {
      if (hasCustomRange) {
        const startDate = new Date(customRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(customRange.endDate);
        endDate.setHours(23, 59, 59, 999);

        // Find the earliest week start covering the range
        const weekStartStr = formatDateLocal(startDate);
        const response = await hrFormService.getHRFormData(weekStartStr);
        const rows = Array.isArray(response?.data) ? response.data : [];
        allRows = rows.filter((item) => {
          const d = new Date(item.startDate);
          return d >= startDate && d <= endDate;
        });

        startLabel = startDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        endLabel = endDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } else {
        for (const weekKey of selectedWeeks) {
          const response = await hrFormService.getHRFormData(weekKey);
          const rows = Array.isArray(response?.data) ? response.data : [];
          allRows = allRows.concat(rows);
        }
        const sortedKeys = Array.from(selectedWeeks).sort();
        startLabel = new Date(sortedKeys[0]).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        endLabel = new Date(
          sortedKeys[sortedKeys.length - 1],
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    } catch (error) {
      console.error("Failed to fetch HR form data for export:", error);
      showError("Failed to load data for the selected period");
      return;
    }

    if (allRows.length === 0) {
      alert("No data found for the selected period");
      return;
    }

    const filename =
      startLabel === endLabel
        ? `HR_Form_${startLabel}.xlsx`
        : `HR_Form_${startLabel}_to_${endLabel}.xlsx`;

    exportToXLSX(allRows, filename);

    setIsDateRangeModalOpen(false);
    setSelectedWeeks(new Set());
    setCustomRange({ startDate: "", endDate: "" });
  };

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <PageWrapper
        title={
          <div className="flex items-center gap-3">
            <span>HR Export Form</span>
            {(isWeekExported || isCurrentWeekClosed) && (
              <Badge variant="warning" className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {isCurrentWeekClosed ? "Week Closed" : "Week Locked"}
              </Badge>
            )}
          </div>
        }
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            <span>Export and view HR form data</span>
            {/* Week navigation: only previous week allowed */}
            <div className="flex items-center gap-1">
              <button
                onClick={handlePreviousWeek}
                disabled={weekOffset === -1 || loading}
                title="Previous week"
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 bg-primary-accent/10 dark:bg-primary-accent/20 text-primary-accent dark:text-orange-400 rounded-lg text-sm font-semibold whitespace-nowrap">
                {weekOffset === -1 ? "Previous Week" : "Current Week"} ·{" "}
                {getCurrentWeekRange()}
              </span>
              <button
                onClick={handleCurrentWeek}
                disabled={weekOffset === 0 || loading}
                title="Current week"
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            {selectedRows.size > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isCurrentWeekClosed || isWeekExported}
                className="group"
              >
                <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">
                  Delete Selected ({selectedRows.size})
                </span>
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchHRFormData(weekOffset)}
              disabled={loading}
              className="group"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenDateRangeModal}
              className="group"
            >
              <FileDown className="w-4 h-4 group-hover:animate-bounce" />
              <span className="hidden sm:inline">Export Previous Forms</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSendEmail}
              disabled={loading || sendingEmail || data.length === 0}
              className="group"
            >
              <Mail className={`w-4 h-4 ${sendingEmail ? "animate-pulse" : ""}`} />
              <span className="hidden sm:inline">
                {sendingEmail ? "Sending..." : "Send by Email"}
              </span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenMailSettings}
              title="Mail recipients (To / CC)"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={handleExportCurrentWeek}
              disabled={loading || data.length === 0}
              className="group"
            >
              <Download className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">
                {weekOffset === 0
                  ? "Export Current Week"
                  : "Export Previous Week"}
              </span>
            </Button>
          </div>
        }
      >
        {/* Mobile Card View */}
        {loading ? (
          <div className="block md:hidden flex flex-col items-center gap-2 py-10">
            <RefreshCw className="w-8 h-8 text-primary-accent animate-spin" />
            <p className="text-sm font-medium text-gray-600 dark:text-light-400">
              Loading HR Form data...
            </p>
          </div>
        ) : data.length === 0 ? (
          <div className="block md:hidden text-center py-10">
            <Calendar className="w-12 h-12 text-gray-400 dark:text-light-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-light-500">
              No approved requests for current week
            </p>
          </div>
        ) : (
          <div className="block md:hidden space-y-3">
            {data.map((row, rowIndex) => (
              <motion.div
                key={row.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: rowIndex * 0.05 }}
                className={`bg-white dark:bg-primary-card rounded-xl border shadow-sm overflow-hidden ${
                  selectedRows.has(row.id)
                    ? "border-primary-accent"
                    : "border-gray-100 dark:border-dark-200"
                } ${isCurrentWeekClosed || isWeekExported ? "opacity-60" : ""}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => {
                        if (!isCurrentWeekClosed && !isWeekExported)
                          handleSelectRow(row.id);
                      }}
                      className="flex items-center gap-2"
                    >
                      {selectedRows.has(row.id) ? (
                        <CheckSquare className="w-4 h-4 text-primary-accent" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                      <span className="text-xs font-bold text-gray-500 dark:text-light-500 uppercase">
                        {row.code}
                      </span>
                    </button>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-accent/10 text-primary-accent font-semibold">
                      {row.timeOffType}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-0.5">
                    {row.employee}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-light-500 mb-3">
                    {row.jobPosition} — {row.branch}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "startDate", label: "Start" },
                      { key: "endDate", label: "End" },
                      { key: "days", label: "Days" },
                    ].map(({ key, label }) => (
                      <div
                        key={key}
                        onClick={() => {
                          if (!isCurrentWeekClosed && !isWeekExported)
                            handleCellClick(row.id, key, row[key]);
                        }}
                        className={`rounded-lg p-2 text-center bg-gray-50 dark:bg-dark-400 ${
                          isCurrentWeekClosed || isWeekExported
                            ? ""
                            : "cursor-pointer hover:bg-primary-accent/10 active:bg-primary-accent/20"
                        }`}
                      >
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          {label}
                        </p>
                        {editingCell?.rowId === row.id &&
                        editingCell?.columnKey === key ? (
                          <input
                            type={
                              key === "startDate" || key === "endDate"
                                ? "date"
                                : "number"
                            }
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="w-full text-xs text-center px-1 py-0.5 bg-white dark:bg-dark-100 border border-primary-accent rounded focus:outline-none"
                          />
                        ) : (
                          <p className="text-xs font-semibold text-gray-900 dark:text-light-100">
                            {row[key] || "—"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {row.reason && (
                    <p className="text-xs text-gray-500 dark:text-light-500 mt-2 italic">
                      "{row.reason}"
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Desktop Table View */}
        <div className="hidden md:block w-full overflow-x-auto scrollbar-thin">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block min-w-full align-middle"
          >
            <div className="overflow-hidden rounded-xl shadow-lg">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-dark-400 dark:to-dark-300">
                  <tr>
                    <th className="px-4 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20 w-12">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleSelectAll}
                        className="flex items-center justify-center w-full"
                        title={
                          selectedRows.size === data.length && data.length > 0
                            ? "Deselect all"
                            : "Select all"
                        }
                      >
                        {selectedRows.size === data.length &&
                        data.length > 0 ? (
                          <CheckSquare className="w-5 h-5 text-primary-accent" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400 dark:text-light-500" />
                        )}
                      </motion.button>
                    </th>
                    {columns.map((column, index) => {
                      const isSortable =
                        column.key === "startDate" || column.key === "endDate";
                      const isActive = sortConfig.key === column.key;
                      const SortIcon = isActive
                        ? sortConfig.dir === "asc"
                          ? ArrowUp
                          : ArrowDown
                        : ArrowUpDown;
                      return (
                        <th
                          key={index}
                          onClick={
                            isSortable
                              ? () => handleDateSort(column.key)
                              : undefined
                          }
                          className={`px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20 ${
                            isSortable
                              ? "cursor-pointer select-none hover:text-primary-accent"
                              : ""
                          }`}
                        >
                          {isSortable ? (
                            <span className="flex items-center gap-1">
                              {column.header}
                              <SortIcon
                                className={`w-3.5 h-3.5 ${
                                  isActive
                                    ? "text-primary-accent"
                                    : "text-gray-400 dark:text-light-500"
                                }`}
                              />
                            </span>
                          ) : (
                            column.header
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody className="bg-white dark:bg-primary-card">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 py-8 text-center text-gray-500 dark:text-light-500"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <RefreshCw className="w-8 h-8 text-primary-accent animate-spin" />
                          <p className="text-sm font-medium">
                            Loading HR Form data...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 py-8 text-center text-gray-500 dark:text-light-500"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <Calendar className="w-12 h-12 text-gray-400 dark:text-light-500" />
                          <p className="text-sm font-medium">
                            No approved requests for current week
                          </p>
                          {getPreviousFormsData.length > 0 && (
                            <p className="text-xs text-gray-400 dark:text-light-500">
                              {getPreviousFormsData.length} previous form(s)
                              available
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    data.map((row, rowIndex) => (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: rowIndex * 0.05 }}
                        className={`transition-colors ${
                          isCurrentWeekClosed || isWeekExported
                            ? "opacity-60 cursor-not-allowed"
                            : "hover:bg-gray-50 dark:hover:bg-dark-400"
                        } ${
                          selectedRows.has(row.id)
                            ? "bg-primary-accent/10 border-l-4 border-l-primary-accent"
                            : ""
                        }`}
                      >
                        <td
                          className="px-4 py-4 text-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCurrentWeekClosed && !isWeekExported) {
                              handleSelectRow(row.id);
                            }
                          }}
                        >
                          <motion.button
                            whileHover={
                              !isCurrentWeekClosed && !isWeekExported
                                ? { scale: 1.1 }
                                : {}
                            }
                            whileTap={
                              !isCurrentWeekClosed && !isWeekExported
                                ? { scale: 0.9 }
                                : {}
                            }
                            className="flex items-center justify-center w-full"
                            disabled={isCurrentWeekClosed || isWeekExported}
                          >
                            {selectedRows.has(row.id) ? (
                              <CheckSquare className="w-5 h-5 text-primary-accent" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400 dark:text-light-500 hover:text-primary-accent transition-colors" />
                            )}
                          </motion.button>
                        </td>
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={`px-6 py-4 text-sm text-gray-700 dark:text-light-200 ${
                              isCurrentWeekClosed || isWeekExported
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
                            onClick={() => {
                              if (!isCurrentWeekClosed && !isWeekExported) {
                                handleCellClick(
                                  row.id,
                                  column.key,
                                  row[column.key],
                                );
                              }
                            }}
                          >
                            {editingCell?.rowId === row.id &&
                            editingCell?.columnKey === column.key ? (
                              <input
                                type={
                                  column.key === "startDate" ||
                                  column.key === "endDate"
                                    ? "date"
                                    : column.key === "days"
                                      ? "number"
                                      : "text"
                                }
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleCellBlur}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                className="w-full px-2 py-1 bg-white dark:bg-dark-100 border border-primary-accent rounded focus:outline-none focus:ring-2 focus:ring-primary-accent text-gray-900 dark:text-light-100"
                              />
                            ) : (
                              <span className="hover:text-primary-accent transition-colors">
                                {row[column.key]}
                              </span>
                            )}
                          </td>
                        ))}
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>

        {/* Mail recipients modal */}
        <BaseModal
          isOpen={isMailSettingsOpen}
          onClose={() => setIsMailSettingsOpen(false)}
          title="Mail Recipients"
          subtitle="Who receives the HR form email (one email per field)"
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsMailSettingsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveMailSettings} disabled={savingMailSettings}>
                {savingMailSettings ? "Saving..." : "Save"}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {[
              ["to", "To"],
              ["cc", "CC"],
              ["bcc", "BCC (hidden)"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-light-300">
                  {label}
                </label>
                <div className="space-y-2">
                  {mailSettings[key].map((email, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="email"
                        dir="ltr"
                        value={email}
                        onChange={(e) => setEmailAt(key, i, e.target.value)}
                        placeholder="name@absegy.com"
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent p-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeEmailRow(key, i)}
                        title="Remove"
                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addEmailRow(key)}
                    className="text-sm font-medium text-primary-accent hover:underline"
                  >
                    + Add email
                  </button>
                </div>
              </div>
            ))}
          </div>
        </BaseModal>

        {/* Date Range Selection Modal */}
        <BaseModal
          isOpen={isDateRangeModalOpen}
          onClose={() => {
            setIsDateRangeModalOpen(false);
            setSelectedWeeks(new Set());
            setCustomRange({ startDate: "", endDate: "" });
          }}
          title="Export Previous Forms"
          subtitle="Select one or more weeks to export"
          size="xl"
          footer={
            <>
              <Button
                variant="bordered"
                onClick={() => {
                  setIsDateRangeModalOpen(false);
                  setSelectedWeeks(new Set());
                  setCustomRange({ startDate: "", endDate: "" });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleExportWithDateRange}>
                <Download className="w-4 h-4" />
                Export to XLSX
              </Button>
            </>
          }
        >
          <div className="space-y-6">
            {/* Week Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-gray-700 dark:text-light-200">
                  Select Weeks to Export
                </label>
                {selectedWeeks.size > 0 && (
                  <span className="text-xs font-medium text-primary-accent">
                    {selectedWeeks.size} week
                    {selectedWeeks.size !== 1 ? "s" : ""} selected
                  </span>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto scrollbar-thin space-y-2">
                {availableWeeks.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-light-500 text-center py-4">
                    No previous weeks available
                  </p>
                ) : (
                  availableWeeks.map((week) => {
                    const weekKey = formatDateLocal(week.start);
                    const isSelected = selectedWeeks.has(weekKey);
                    return (
                      <motion.button
                        key={weekKey}
                        whileHover={{ x: 4 }}
                        onClick={() => {
                          if (week.status !== "available") return;
                          setSelectedWeeks((prev) => {
                            const next = new Set(prev);
                            if (next.has(weekKey)) next.delete(weekKey);
                            else next.add(weekKey);
                            return next;
                          });
                        }}
                        disabled={week.status === "not_available"}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          isSelected
                            ? "border-primary-accent bg-primary-accent/10 text-primary-accent"
                            : week.status === "available"
                              ? "border-gray-200 dark:border-dark-200 bg-white dark:bg-primary-card text-gray-700 dark:text-light-200 hover:border-primary-accent/50 cursor-pointer"
                              : "border-gray-200 dark:border-dark-200 bg-gray-50 dark:bg-dark-100 text-gray-400 dark:text-light-500 cursor-not-allowed opacity-60"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold">{week.label}</p>
                              <Badge
                                variant={
                                  week.status === "available"
                                    ? "success"
                                    : "default"
                                }
                                className="text-xs"
                              >
                                {week.status === "available" ? (
                                  <>
                                    <CheckCircle2 className="w-3 h-3" />
                                    Available
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3 h-3" />
                                    Not Available
                                  </>
                                )}
                              </Badge>
                            </div>
                            {week.status === "available" && (
                              <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                                {week.count} form{week.count !== 1 ? "s" : ""}{" "}
                                available
                              </p>
                            )}
                          </div>
                          <div
                            className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-all ${
                              isSelected
                                ? "bg-primary-accent border-primary-accent"
                                : "border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-100"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Custom Date Range */}
            <div className="pt-4 border-t border-gray-200 dark:border-dark-100 space-y-3">
              <label className="block text-sm font-semibold text-gray-700 dark:text-light-200">
                Or Export by Custom Date Range
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-light-400 mb-2">
                    From
                  </label>
                  <input
                    type="date"
                    value={customRange.startDate}
                    onChange={(e) =>
                      setCustomRange((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    max={customRange.endDate || formatDateLocal(new Date())}
                    className="w-full px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-light-400 mb-2">
                    To
                  </label>
                  <input
                    type="date"
                    value={customRange.endDate}
                    onChange={(e) =>
                      setCustomRange((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    min={customRange.startDate}
                    max={formatDateLocal(new Date())}
                    className="w-full px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </BaseModal>

        {/* Employee Details Modal */}
        <BaseModal
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedEmployeeDetails(null);
          }}
          title="Employee Time-Off Details"
          subtitle={
            selectedEmployeeDetails
              ? `${selectedEmployeeDetails.name} - ${selectedEmployeeDetails.jobPosition}`
              : ""
          }
          size="xl"
        >
          {selectedEmployeeDetails && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="bg-gradient-to-r from-primary-accent/10 to-orange-600/5 rounded-lg p-4 border border-primary-accent/20">
                <div className="flex items-center gap-3">
                  <User className="w-6 h-6 text-primary-accent" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-light-100">
                      {selectedEmployeeDetails.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-light-400">
                      {selectedEmployeeDetails.jobPosition} •{" "}
                      {selectedEmployeeDetails.branch}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mission Dates */}
              {selectedEmployeeDetails.mission.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-light-100">
                      Mission Dates ({selectedEmployeeDetails.mission.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedEmployeeDetails.mission.map((item, index) => (
                      <div
                        key={index}
                        className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatDateWithWeekday(item.startDate)}
                          {item.endDate &&
                            item.endDate !== item.startDate &&
                            ` - ${formatDateWithWeekday(item.endDate)}`}
                        </p>
                        {item.reason && (
                          <p className="text-xs text-gray-600 dark:text-light-400 mt-1">
                            {item.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Vacation Dates */}
              {selectedEmployeeDetails.vacation.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Umbrella className="w-5 h-5 text-green-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-light-100">
                      Vacation Dates ({selectedEmployeeDetails.vacation.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedEmployeeDetails.vacation.map((item, index) => (
                      <div
                        key={index}
                        className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatDateWithWeekday(item.startDate)}
                          {item.endDate &&
                            item.endDate !== item.startDate &&
                            ` - ${formatDateWithWeekday(item.endDate)}`}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-light-400 mt-1">
                          Days: {item.days || 1}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Early Leave Dates */}
              {selectedEmployeeDetails.earlyLeave.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-light-100">
                      Early Leave Dates (
                      {selectedEmployeeDetails.earlyLeave.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedEmployeeDetails.earlyLeave.map((item, index) => (
                      <div
                        key={index}
                        className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatDateWithWeekday(item.startDate)}
                        </p>
                        {item.reason && (
                          <p className="text-xs text-gray-600 dark:text-light-400 mt-1">
                            {item.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Late Permission Dates */}
              {selectedEmployeeDetails.latePermission.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-light-100">
                      Late Permission Dates (
                      {selectedEmployeeDetails.latePermission.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedEmployeeDetails.latePermission.map(
                      (item, index) => (
                        <div
                          key={index}
                          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
                        >
                          <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                            {formatDateWithWeekday(item.startDate)}
                          </p>
                          {item.reason && (
                            <p className="text-xs text-gray-600 dark:text-light-400 mt-1">
                              {item.reason}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* WFH Dates */}
              {selectedEmployeeDetails.wfh.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Home className="w-5 h-5 text-blue-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-light-100">
                      Work From Home Dates ({selectedEmployeeDetails.wfh.length}
                      )
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedEmployeeDetails.wfh.map((item, index) => (
                      <div
                        key={index}
                        className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatDateWithWeekday(item.startDate)}
                          {item.endDate &&
                            item.endDate !== item.startDate &&
                            ` - ${formatDateWithWeekday(item.endDate)}`}
                        </p>
                        {item.reason && (
                          <p className="text-xs text-gray-600 dark:text-light-400 mt-1">
                            {item.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No data message */}
              {selectedEmployeeDetails.mission.length === 0 &&
                selectedEmployeeDetails.vacation.length === 0 &&
                selectedEmployeeDetails.earlyLeave.length === 0 &&
                selectedEmployeeDetails.latePermission.length === 0 &&
                selectedEmployeeDetails.wfh.length === 0 && (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 dark:text-light-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-light-400">
                      No time-off records for this employee
                    </p>
                  </div>
                )}
            </div>
          )}
        </BaseModal>
      </PageWrapper>

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDeleteSelected();
        }}
        title="Delete Selected Forms"
        message={`Are you sure you want to delete ${selectedRows.size} form(s)? This will delete the underlying requests. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default HRForm;
